// components/operations/IssueDetail.tsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  UserRound,
  XCircle,
  MailIcon,
  CalendarClock,
  LifeBuoy,
} from "lucide-react";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import {
  IssueDetail as IssueDetailType,
  OverrideIssueRequest,
} from "@/types/operations";
import { formatDateToBritish } from "@/lib/dateUtils";
import DashboardContainer from "@/components/layout/DashboardContainer";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";

const IssueDetail: React.FC = () => {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [issueDetail, setIssueDetail] = useState<IssueDetailType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [justification, setJustification] = useState<string>("");

  useEffect(() => {
    const fetchIssueDetail = async () => {
      if (!issueId) return;

      try {
        setIsLoading(true);
        setError(null);
        const data = await OperationsDashboardService.getIssueDetail(
          Number(issueId),
        );
        setIssueDetail(data);
      } catch (err) {
        setError("Failed to load issue details. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIssueDetail();
  }, [issueId]);

  // Handle override issue
  const handleOverrideIssue = async () => {
    if (!issueId || !justification.trim()) return;

    try {
      setIsSubmitting(true);
      const data: OverrideIssueRequest = {
        justification: justification.trim(),
      };
      const updatedIssue = await OperationsDashboardService.overrideIssue(
        Number(issueId),
        data,
      );
      setIssueDetail(updatedIssue);
      toast({
        title: "Issue overridden successfully",
        description: "The issue has been marked as overridden.",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Failed to override issue",
        description:
          "An error occurred while overriding the issue. Please try again.",
        variant: "destructive",
      });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigate back
  const handleBackClick = () => {
    navigate("/operations/dashboard");
  };

  // Format issue date with appropriate styling
  const getFormattedDate = (dateString: string) => {
    return (
      <div className="flex items-center text-sm">
        <Calendar className="w-4 h-4 mr-1 text-gray-500" />
        {formatDateToBritish(dateString)}
      </div>
    );
  };

  // Get severity badge with appropriate styling
  const getSeverityBadge = (severity: string) => {
    if (severity === "CRITICAL") {
      return (
        <Badge variant="destructive" className="flex items-center">
          <AlertTriangle className="w-3 h-3 mr-1" />
          {severity}
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="flex items-center border-yellow-500 text-yellow-700"
      >
        <Clock className="w-3 h-3 mr-1" />
        {severity}
      </Badge>
    );
  };

  // Get status badge with appropriate styling
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return (
          <Badge
            variant="outline"
            className="flex items-center bg-blue-100 border-blue-500 text-blue-700"
          >
            <CalendarClock className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        );
      case "resolved":
        return (
          <Badge className="flex items-center bg-green-100 border-green-500 text-green-700">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        );
      case "overridden":
        return (
          <Badge className="flex items-center bg-purple-100 border-purple-500 text-purple-700">
            <LifeBuoy className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <DashboardContainer>
        <div className="flex justify-center items-center py-8">
          <p className="text-gray-500">Loading issue details...</p>
        </div>
      </DashboardContainer>
    );
  }

  if (error || !issueDetail) {
    return (
      <DashboardContainer>
        <div className="flex justify-center items-center py-8">
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
            <p className="flex items-center">
              <XCircle className="w-5 h-5 mr-2" />
              {error || "Issue not found"}
            </p>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  const isActive = issueDetail.status.toLowerCase() === "active";

  return (
    <DashboardContainer
      title={`Issue: ${issueDetail.issue_type}`}
      headerContent={
        <Button
          variant="outline"
          size="sm"
          className="flex items-center"
          onClick={handleBackClick}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Issue Summary Card */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>Issue Summary</CardTitle>
                <CardDescription>
                  Overview of the operational issue
                </CardDescription>
              </div>
              {getStatusBadge(issueDetail.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Issue Type
                  </h3>
                  <p className="mt-1 font-medium">{issueDetail.issue_type}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Severity
                  </h3>
                  <div className="mt-1">
                    {getSeverityBadge(issueDetail.severity)}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Created At
                  </h3>
                  <div className="mt-1">
                    {getFormattedDate(issueDetail.created_at)}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Employer
                  </h3>
                  <p className="mt-1 flex items-center">
                    <MailIcon className="w-4 h-4 mr-1 text-gray-500" />
                    {issueDetail.employer_email}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Employee
                  </h3>
                  <p className="mt-1 flex items-center">
                    <UserRound className="w-4 h-4 mr-1 text-gray-500" />
                    {issueDetail.employee_name}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Description
                  </h3>
                  <p className="mt-1">{issueDetail.description}</p>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row gap-3 justify-end border-t pt-6">
            {isActive && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="w-full sm:w-auto">
                    Override Issue
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Override Issue</DialogTitle>
                    <DialogDescription>
                      Please provide a justification for overriding this issue.
                      Your justification will be recorded in the audit log.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Textarea
                      placeholder="Enter justification..."
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                      className="min-h-[100px]"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      The justification should clearly explain why this issue
                      can be bypassed despite the potential risks.
                    </p>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                      onClick={handleOverrideIssue}
                      disabled={isSubmitting || !justification.trim()}
                    >
                      {isSubmitting ? "Processing..." : "Submit Override"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            <Button
              className="w-full sm:w-auto"
              onClick={() =>
                navigate(`/operations/employers/${issueDetail.employer_id}`)
              }
            >
              View All Employer Issues
            </Button>
          </CardFooter>
        </Card>

        {/* Payrun Information Card (if available) */}
        {issueDetail.payrun_info && (
          <Card>
            <CardHeader>
              <CardTitle>Payrun Information</CardTitle>
              <CardDescription>
                Details about the related payrun
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Payrun ID
                    </h3>
                    <p className="mt-1 font-medium">
                      {issueDetail.payrun_info.id}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Pay Date
                    </h3>
                    <div className="mt-1">
                      {issueDetail.payrun_info.payDate
                        ? getFormattedDate(issueDetail.payrun_info.payDate)
                        : "N/A"}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Status
                    </h3>
                    <p className="mt-1 capitalize">
                      <Badge
                        className={
                          issueDetail.payrun_info.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : issueDetail.payrun_info.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                        }
                      >
                        {issueDetail.payrun_info.status || "N/A"}
                      </Badge>
                    </p>
                  </div>
                </div>

                {issueDetail.payrun_info.calculations && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">
                        Calculations
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-md overflow-auto">
                        <pre className="text-xs">
                          {JSON.stringify(
                            issueDetail.payrun_info.calculations,
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Version History Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Version History
            </CardTitle>
            <CardDescription>History of changes to this issue</CardDescription>
          </CardHeader>
          <CardContent>
            {issueDetail.version_history.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No version history available
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">
                      Description
                    </TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead className="hidden md:table-cell">
                      Change Reason
                    </TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issueDetail.version_history.map((version) => (
                    <TableRow key={version.version}>
                      <TableCell>{version.version}</TableCell>
                      <TableCell>{getStatusBadge(version.status)}</TableCell>
                      <TableCell className="hidden md:table-cell max-w-xs truncate">
                        {version.description}
                      </TableCell>
                      <TableCell>{version.changed_by}</TableCell>
                      <TableCell className="hidden md:table-cell max-w-xs truncate">
                        {version.change_reason}
                      </TableCell>
                      <TableCell>
                        {formatDateToBritish(version.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Action Warnings */}
        {issueDetail.severity === "CRITICAL" && isActive && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Critical Issue Warning</AlertTitle>
            <AlertDescription>
              This is a critical issue that may affect payroll processing. Only
              override this issue if you fully understand the implications.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </DashboardContainer>
  );
};

export default IssueDetail;
